'use client'

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { DASHBOARD_THEME_KEY, isThemePreference, parseThemePreference, type ThemePreference } from '@/lib/dashboardTheme'
import { useBarDataContext } from '@/contexts/BarDataContext'
import { useAuthContext } from '@/contexts/AuthContext'
import { getUserToken } from '@/lib/utils/misc'

const DashboardThemeContext = createContext({
  preference: 'light' as ThemePreference,
  mode: 'light' as 'light' | 'dark',
  setPreference: (_preference: ThemePreference) => {}
})

export const useDashboardTheme = () => useContext(DashboardThemeContext)

export function DashboardThemeProvider ({ children }: { children: ReactNode }) {
  const { barData } = useBarDataContext()
  const { user } = useAuthContext()
  const [preference, setPreferenceState] = useState<ThemePreference>('light')
  const [mode, setMode] = useState<'light' | 'dark'>('light')
  const [theme, setTheme] = useState(() => createTheme())
  const initializedBarId = useRef<string | null>(null)
  const saveQueue = useRef<Promise<void>>(Promise.resolve())

  const applyPreference = useCallback((next: ThemePreference) => {
    try { localStorage.setItem(DASHBOARD_THEME_KEY, next) } catch {}
    window.dispatchEvent(new CustomEvent('dashboard-theme-change', { detail: next }))
  }, [])

  const persistPreference = useCallback((next: ThemePreference, recordActivity = true) => {
    saveQueue.current = saveQueue.current
      .catch(() => {})
      .then(async () => {
        if (!user) return
        const token = await getUserToken(user)
        const response = await fetch('/api/dashboard-theme', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ preference: next, recordActivity })
        })
        if (!response.ok) throw new Error(`Failed to save dashboard theme (${response.status})`)
      })
      .catch(error => console.error('No se pudo guardar la preferencia del tema:', error))
  }, [user])

  const setPreference = useCallback((next: ThemePreference) => {
    applyPreference(next)
    persistPreference(next)
  }, [applyPreference, persistPreference])

  useEffect(() => {
    if (!barData?._id) return
    const barId = String(barData._id)
    if (initializedBarId.current === barId) return
    initializedBarId.current = barId

    // The Bar value is authoritative. Bars created before this field existed
    // keep their previous browser preference, then migrate it on first load.
    if (isThemePreference(barData.dashboardTheme)) {
      applyPreference(barData.dashboardTheme)
      return
    }

    let legacyPreference: string | null = null
    try { legacyPreference = localStorage.getItem(DASHBOARD_THEME_KEY) } catch {}
    if (isThemePreference(legacyPreference)) {
      applyPreference(legacyPreference)
      persistPreference(legacyPreference, false)
    } else {
      applyPreference('light')
      persistPreference('light', false)
    }
  }, [applyPreference, barData?._id, barData?.dashboardTheme, persistPreference])

  useLayoutEffect(() => {
    const root = document.documentElement
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const meta = document.querySelector('meta[name="theme-color"]')
    const previousMeta = meta?.getAttribute('content')
    const schemeMeta = document.querySelector('meta[name="color-scheme"]')
    const previousScheme = schemeMeta?.getAttribute('content')
    let selected: ThemePreference = 'light'
    try { selected = parseThemePreference(localStorage.getItem(DASHBOARD_THEME_KEY)) } catch {}
    // App Router may stream/replace metadata after this provider has mounted.
    const syncMetadata = () => {
      const values = {
        'theme-color': getComputedStyle(root).getPropertyValue('--color-surface-secondary').trim(),
        'color-scheme': root.dataset.theme || 'light'
      }
      for (const [name, value] of Object.entries(values)) {
        const element = document.querySelector(`meta[name="${name}"]`)
        if (element && element.getAttribute('content') !== value) element.setAttribute('content', value)
      }
    }
    const observer = new MutationObserver(syncMetadata)
    observer.observe(document.head, { childList: true, subtree: true, attributes: true, attributeFilter: ['content'] })

    const apply = () => {
      const next = selected === 'system' ? (media.matches ? 'dark' : 'light') : selected
      root.dataset.theme = next
      setPreferenceState(selected)
      setMode(next)
      const styles = getComputedStyle(root)
      const color = (name: string) => styles.getPropertyValue(`--color-${name}`).trim()
      syncMetadata()
      setTheme(createTheme({
        palette: {
          mode: next,
          ...(next === 'dark' ? {
            // MUI otherwise darkens contained-button hovers automatically,
            // reducing contrast against the dark action text.
            primary: { main: color('primary'), dark: color('primary-hover'), contrastText: color('on-primary') },
            secondary: { main: color('premium-text'), dark: color('premium-strong'), contrastText: color('on-primary') },
            error: { main: color('danger-text'), dark: color('danger-strong'), contrastText: color('on-primary') },
            warning: { main: color('warning-text'), dark: color('warning-strong'), contrastText: color('on-primary') },
            info: { main: color('info-text'), dark: color('info-stronger'), contrastText: color('on-primary') },
            success: { main: color('success-text'), dark: color('success-strong'), contrastText: color('on-primary') },
            background: { default: color('surface-secondary'), paper: color('surface-elevated') },
            text: { primary: color('text'), secondary: color('text-secondary'), disabled: color('text-muted') },
            divider: color('border')
          } : {})
        },
        ...(next === 'dark' ? {
          components: {
            MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
            MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: color('surface-tertiary'), color: color('text'), border: `1px solid ${color('border-strong')}` } } }
          }
        } : {})
      }))
    }
    const onPreference = (event: Event) => {
      selected = (event as CustomEvent<ThemePreference>).detail
      apply()
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === DASHBOARD_THEME_KEY || event.key === null) {
        selected = parseThemePreference(event.newValue)
        apply()
      }
    }
    apply()
    media.addEventListener('change', apply)
    window.addEventListener('storage', onStorage)
    window.addEventListener('dashboard-theme-change', onPreference)
    return () => {
      observer.disconnect()
      media.removeEventListener('change', apply)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('dashboard-theme-change', onPreference)
      delete root.dataset.theme
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', previousMeta ?? '#ffffff')
      document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', previousScheme ?? 'light')
    }
  }, [])

  return (
    <DashboardThemeContext.Provider value={{ preference, mode, setPreference }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </DashboardThemeContext.Provider>
  )
}
